import React, { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import { brand } from '../../theme';
import { formatTimecode } from '../../utils/timecode';
import { getAnnotationCategory } from '../../utils/annotationCategories';
import StatusChip from '../ui/StatusChip';
import EmptyState from '../ui/EmptyState';
import type { ProjectTask, ReviewAnnotation } from './types';

interface ReviewNotesPanelProps {
  notes: ReviewAnnotation[];
  tasks: ProjectTask[];
  fps: number;
  /** Shows a moment of the original footage; omitted when there is none to show. */
  onShowInFootage?: (time: number) => void;
}

const TIMECODE_FONT = '"JetBrains Mono", "SFMono-Regular", Menlo, Consolas, monospace';

/**
 * The creator's notes on the original footage, in time order, each with the
 * task it became and that task's status, so the creator can check a cut
 * against what they asked for.
 */
const ReviewNotesPanel: React.FC<ReviewNotesPanelProps> = ({ notes, tasks, fps, onShowInFootage }) => {
  const sorted = useMemo(
    () => notes.filter((note) => note && Number.isFinite(Number(note.time))).sort((a, b) => a.time - b.time),
    [notes]
  );

  const taskForNote = useMemo(() => {
    const map = new Map<string, ProjectTask>();
    tasks.forEach((task) => {
      (task.generatedFromAnnotations || []).forEach((noteId) => {
        if (!map.has(noteId)) map.set(noteId, task);
      });
    });
    return map;
  }, [tasks]);

  if (sorted.length === 0) {
    return (
      <EmptyState
        compact
        icon={<RateReviewOutlinedIcon />}
        title="No review notes"
        description="Notes you add to the footage in the annotation studio appear here."
      />
    );
  }

  return (
    <Box component="ol" aria-label="Review notes" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {sorted.map((note, index) => {
        const start = formatTimecode(note.time, fps);
        const end = typeof note.endTime === 'number' && note.endTime > note.time ? formatTimecode(note.endTime, fps) : null;
        const category = note.category ? getAnnotationCategory(note.category, note.color) : null;
        const task = taskForNote.get(note.id);
        return (
          <Box
            component="li"
            key={note.id || `${note.time}-${index}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '132px minmax(0, 1fr)' },
              columnGap: 2,
              rowGap: 0.5,
              px: 2.5,
              py: 1.75,
              borderTop: index === 0 ? 'none' : `1px solid ${brand.border}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', columnGap: 0.5 }}>
              {onShowInFootage ? (
                <Button
                  size="small"
                  onClick={() => onShowInFootage(note.time)}
                  aria-label={`Show ${start} in the original footage`}
                  sx={{ minWidth: 0, px: 0.75, ml: -0.75, fontFamily: TIMECODE_FONT, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}
                >
                  {start}
                </Button>
              ) : (
                <Typography sx={{ fontFamily: TIMECODE_FONT, fontSize: 13, fontVariantNumeric: 'tabular-nums', py: 0.5 }}>
                  {start}
                </Typography>
              )}
              {end && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontFamily: TIMECODE_FONT, fontVariantNumeric: 'tabular-nums', width: '100%' }}
                >
                  to {end}
                </Typography>
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              {category && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                  <Box aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: category.color }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {category.label}
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {note.text}
              </Typography>
              {task && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                  <StatusChip kind="task" status={task.status} />
                  <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                    Task: {task.title}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ReviewNotesPanel;
